import React, { useState } from 'react';
import { BiLike, BiSolidLike, BiDislike, BiSolidDislike } from 'react-icons/bi';
import { IoCopyOutline, IoCheckmarkOutline } from 'react-icons/io5';
import styled from 'styled-components';
import { MessageVote } from '../../services/api';
import { colors, contentColumnPadding } from '../../theme/constants';

interface Props {
  messageId: string | number;
  onCopy: () => void;
  vote?: MessageVote | string | null;
  onVote?: (vote: MessageVote) => void;
}

// The DB stores 'liked'/'disliked' (ActiveRecord enum names) while the frontend enum
// uses 'like'/'dislike'. Normalize both so comparisons work after a page/conversation reload.
function normalizeVote(v?: string | null): MessageVote {
  if (v === MessageVote.LIKE   || v === 'liked')   return MessageVote.LIKE;
  if (v === MessageVote.DISLIKE || v === 'disliked') return MessageVote.DISLIKE;
  return MessageVote.NEUTRAL;
}

const MessageActionsBar: React.FC<Props> = ({ messageId, onCopy, vote, onVote }) => {
  const [copied, setCopied] = useState(false);

  const currentVote = normalizeVote(vote);

  const handleCopy = async () => {
    try {
      onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API may fail in non-secure contexts
    }
  };

  const handleVote = (v: MessageVote) => {
    onVote?.(v);
  };

  return (
    <Bar>
      <ActionButton
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy response'}
        aria-label={copied ? 'Copied' : 'Copy response'}
        $active={copied}
      >
        {copied ? <IoCheckmarkOutline size={15} /> : <IoCopyOutline size={15} />}
      </ActionButton>

      {onVote && (
        <>
          <Divider />
          <ActionButton
            onClick={() => handleVote(MessageVote.LIKE)}
            title="Helpful"
            aria-label="Mark as helpful"
            $active={currentVote === MessageVote.LIKE}
            $activeColor={colors.primary}
            key={`like-${messageId}`}
          >
            {currentVote === MessageVote.LIKE ? (
              <BiSolidLike size={15} />
            ) : (
              <BiLike size={15} />
            )}
          </ActionButton>
          <ActionButton
            onClick={() => handleVote(MessageVote.DISLIKE)}
            title="Not helpful"
            aria-label="Mark as not helpful"
            $active={currentVote === MessageVote.DISLIKE}
            $activeColor={colors.errorBorder}
            key={`dislike-${messageId}`}
          >
            {currentVote === MessageVote.DISLIKE ? (
              <BiSolidDislike size={15} />
            ) : (
              <BiDislike size={15} />
            )}
          </ActionButton>
        </>
      )}
    </Bar>
  );
};

export default MessageActionsBar;

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 6px;
`;

const Divider = styled.div`
  width: 1px;
  height: 14px;
  background: ${({ theme }) => theme.av2.border};
  margin: 0 4px;
`;

const ActionButton = styled.button<{ $active?: boolean; $activeColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ theme, $active, $activeColor }) =>
    $active ? ($activeColor || theme.av2.primary) : theme.av2.textMuted};
  transition: background 0.12s ease, color 0.12s ease;

  &:hover {
    background: ${({ theme }) => theme.av2.hoverBg};
    color: ${({ theme, $active, $activeColor }) =>
      $active ? ($activeColor || theme.av2.primary) : theme.av2.textSecondary};
  }
`;
